import { css, useTheme } from '@emotion/react';
import { breakpoints } from '@birdi/theme';
import {
  Tabs,
  Tab,
  CommentText,
  Avatar,
  Spinner,
  UnreadBadge,
} from '@birdi/components-common';
import { organisation as organisationApi, stroot } from '@birdi/js-sdk';
import MapSVG from '@birdi/icons/map.svg';
import { formatISO, intlFormatDistance } from 'date-fns';
import React, { useEffect, useState, useMemo } from 'react';
import { useMetaSelector } from '../../meta/reducer';
import { OrganisationComment } from '@birdi/js-sdk/src/public/organisation';
import { posthog } from '../../lib/posthog';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

const COMMENTS_TO_SHOW = 5;

export const RecentComments: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const [unreadMentions, setUnreadMentions] = useState(0);

  useEffect(() => {
    organisationApi
      .getUnreadCommentMentions(stroot('ir1mah'))
      .then((res) => setUnreadMentions(res.body?.count ?? 0))
      .catch((err) => console.error('failed to get unread mentions', err));
  }, []);

  const tabs = useMemo(
    (): Tab[] => [
      {
        id: 'all',
        label: 'All comments',
        content: () => (
          <CommentsList
            mentionsOnly={false}
            empty={{
              title: 'No recent comments',
              text: 'Comments from your team will appear here as they collaborate on maps.',
            }}
          />
        ),
      },
      {
        id: 'mentioned',
        label: (
          <span
            css={css`
              position: relative;
            `}
          >
            Mentions
            <UnreadBadge count={unreadMentions} horizontalOffset={-12} />
          </span>
        ),
        content: () => (
          <CommentsList
            mentionsOnly={true}
            empty={{
              title: 'No mentions yet',
              text: 'When team members mention you in comments, they will appear here.',
            }}
          />
        ),
      },
    ],
    [unreadMentions],
  );

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
        id={suppressIntercomAnchor ? undefined : 'latest-comments'}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Latest comments & mentions</span>
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
        `}
      >
        <Tabs tabs={tabs} variant="large" />
      </div>
    </div>
  );
};

interface CommentsListProps {
  mentionsOnly: boolean;
  empty: { title: string; text: string };
}

const CommentsList = ({ mentionsOnly, empty }: CommentsListProps) => {
  const theme = useTheme();
  const meta = useMetaSelector();
  const [comments, setComments] = useState<OrganisationComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);

  const fetchComments = async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setError(null);
        setCursor(null);
      } else {
        setLoadingMore(true);
      }

      const options: any = {
        limit: COMMENTS_TO_SHOW,
        mentionsOnly,
      };

      if (!reset && cursor) {
        options.cursor = new Date(cursor);
      }

      const response = await organisationApi.getRecentComments(
        stroot('ait3ia'),
        options,
      );

      if (response.status === 200 && response.body?.comments) {
        // Convert createdAt strings to Date objects and ensure we have all required fields
        const newComments = response.body.comments.map((comment: any) => ({
          ...comment,
          createdAt:
            typeof comment.createdAt === 'string'
              ? comment.createdAt
              : new Date(comment.createdAt).toISOString(),
        }));

        if (reset) {
          setComments(newComments);
        } else {
          setComments((prev) => [...prev, ...newComments]);
        }

        setHasMore(response.body.hasMore || false);
        setCursor(response.body.nextCursor);
      } else {
        setError('Failed to load comments');
      }
    } catch (err) {
      console.error('Error fetching recent comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreComments = () => {
    if (!loadingMore && hasMore) {
      posthog?.capture('dashboard comments expand clicked');
      fetchComments(false);
    }
  };

  useEffect(() => {
    fetchComments(true);
  }, [mentionsOnly]);

  if (loading) {
    return (
      <div
        css={css`
          padding: 1em;
          display: flex;
          justify-content: center;
          align-items: center;
          flex: 1;
        `}
      >
        <Spinner color={theme.deepOcean} size="2em" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        css={css`
          padding: 1em;
          text-align: center;
          color: ${theme.mono50};
        `}
      >
        {error}
      </div>
    );
  }

  if (!comments.length) {
    return (
      <div
        css={css`
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          flex: 1;
        `}
      >
        <div>
          <strong>{empty.title}:</strong> {empty.text}
        </div>
      </div>
    );
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        flex: 1;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 1em;
          overflow-y: auto;
          flex: 1;
        `}
      >
        {comments.map((comment) => (
          <CommentCard key={comment.id} comment={comment} />
        ))}
        {loadingMore && (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              margin: 1em 0;
            `}
          >
            <Spinner color={theme.deepOcean} size="1.5em" />
          </div>
        )}
      </div>
      {hasMore && !loading && (
        <div
          css={css`
            padding: 1em;
          `}
        >
          <button
            disabled={loadingMore}
            onClick={loadMoreComments}
            css={css`
              width: 100%;
              color: ${loadingMore ? theme.mono40 : theme.mono60};
              border: solid 1px ${theme.mono30};
              border-radius: 5px;
              padding: 0.5em;
              font-weight: 500;
              cursor: ${loadingMore ? 'not-allowed' : 'pointer'};
              background: ${theme.mono0};

              &:hover:not(:disabled) {
                background: ${theme.mono10};
              }
            `}
          >
            {loadingMore ? (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  gap: 0.5em;
                `}
              >
                <Spinner color={theme.deepOcean} size="1em" />
                Loading...
              </div>
            ) : (
              'Load more'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

interface CommentCardProps {
  comment: OrganisationComment;
}

const CommentCard = ({ comment }: CommentCardProps) => {
  const theme = useTheme();
  const meta = useMetaSelector();

  const createdAtDisplay = useMemo(() => {
    const text = intlFormatDistance(new Date(comment.createdAt), new Date());
    return text[0].toUpperCase() + text.slice(1);
  }, [comment.createdAt]);

  const createdAtDatetime = useMemo(
    () => formatISO(new Date(comment.createdAt)),
    [comment.createdAt],
  );

  const isMentioned = useMemo(
    () => comment.mentions.some((mention) => mention.id === meta.id),
    [comment.mentions, meta.id],
  );

  const handleCommentClick = () => {
    posthog?.capture('dashboard comment clicked', { commentId: comment.id });
  };

  return (
    <a
      href={`/map/next/${comment.mapId}?comment=${comment.id}`}
      onClick={handleCommentClick}
      css={css`
        display: grid;
        grid-template-columns: 32px 1fr;
        grid-template-rows: auto auto;
        column-gap: 0.75rem;
        border-radius: 6px;
        background-color: ${comment.unread ? theme.lightFlora : '#fff'};
        padding: 0.5rem 0.75rem;
        cursor: pointer;
        text-decoration: none;
        color: inherit;

        &:hover,
        &:focus {
          background-color: ${theme.sand};
        }
      `}
    >
      <div
        css={css`
          grid-row: 2;
          grid-column: 1;
        `}
      >
        {comment.author && (
          <Avatar
            id={comment.author.id}
            profilePic={comment.author.profilePic}
            name={`${comment.author.fname} ${comment.author.lname}`}
          />
        )}
      </div>

      <header
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-weight: 700;
          grid-row: 1;
          grid-column: 2;
        `}
      >
        <span>
          {isMentioned
            ? `${comment.author?.fname} ${comment.author?.lname} mentioned you`
            : `${comment.author?.fname} ${comment.author?.lname} left a comment`}
        </span>
      </header>

      <div
        css={css`
          /* flex: 1; */
          grid-row: 2;
          grid-column: 2;
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 1rem;
            min-height: 32px;
          `}
        >
          <div
            css={css`
              flex: 1;
              line-height: 1.4;
              overflow: hidden;
            `}
          >
            <CommentText text={comment.text} mentions={comment.mentions} />
          </div>
        </div>

        <footer
          css={css`
            display: flex;
            color: ${theme.darkGrey};
            font-size: 0.9em;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              display: inline-flex;
              align-items: center;
              gap: 0.25rem;
            `}
          >
            <MapSVG /> {comment.mapName}
          </span>

          <time
            dateTime={createdAtDatetime}
            css={css`
              color: ${theme.darkGrey};
              font-weight: normal;
              font-size: 0.9em;
            `}
          >
            {createdAtDisplay}
          </time>
        </footer>
      </div>
    </a>
  );
};
